import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Icon,
  Tooltip,
  TextField,
  MenuItem,
  FormControl,
  Select,
  RadioGroup,
  Radio,
  FormControlLabel,
  Autocomplete,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import {
  STATUS,
  GENDER,
  DEFAULT_RECORDS_PER_PAGE,
  hasAccess,
} from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  autoCompleteProps,
  ISOToDDMMYYYY,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { getDepartmentList } from "actions/department";
import { designationList } from "constants/designation";
import { getDesignationList } from "actions/designation";
import { useDispatch, useSelector } from "react-redux";
import {
  getTechnicianList,
  addTechnician,
  getTechnicianById,
  updateTechnicianById,
  deleteTechnicianById,
  importTechnician,
  exportTechnicianData,
} from "actions/technician";
import { departmentList } from "constants/department";
import { technicianList } from "constants/technician";
import { getContractorList } from "actions/contractor";
import { contractorList } from "constants/contractor";

const initialFields = {
  id: null,
  name: { value: "", original: "", error: "" },
  password: { value: "", original: "", error: "" },
  employeeID: { value: "", original: "", error: "" },
  deptId: { value: null, original: "", error: "" },
  gender: { value: "", original: "", error: "" },
  contractorId: { value: null, original: "", error: "" },
  designation: { value: null, original: "", error: "" },
  status: { value: "ACTIVE", original: "ACTIVE", error: "" },
};

function TechnicianListing() {
  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("TECHNICIAN_MASTER", "CREATE")?.includes(
    currentRole,
  );
  const canUpdate = hasAccess("TECHNICIAN_MASTER", "UPDATE")?.includes(
    currentRole,
  );
  const canDelete = hasAccess("TECHNICIAN_MASTER", "DELETE")?.includes(
    currentRole,
  );
  const canShowActions = canUpdate || canDelete;

  const dispatch = useDispatch();
  const technicianListData = useSelector(
    (state) => state.technician.technicianList,
  );
  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );
  const contractorListData = useSelector(
    (state) => state.contractor.contractorList,
  );
  const designationListData = useSelector(
    (state) => state.designation.designationList,
  );
  const [pageNum, setPageNum] = useState(1);
  const [panelMode, setPanelMode] = useState(null);
  const [techFormData, setTechFormData] = useState(initialFields);
  const { notify } = useNotify();
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filters, setFilters] = useState({
    name: "",
    department: "",
    status: "",
    filterParams: [],
  });
  const [activeTab, setActiveTab] = useState("info");
  const [show, setShow] = useState({
    password: false,
  });

  const [exportLoading, setExportLoading] = useState(false);

  const contractorOptions = contractorListData?.data?.Contractor || [];

  useEffect(() => {
    getTechnicianList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({ type: technicianList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getDepartmentList(dispatch);
    getContractorList(dispatch);
    getDesignationList(dispatch);

    return () => {
      dispatch({ type: designationList, payload: {} });
      dispatch({ type: departmentList, payload: {} });
      dispatch({ type: contractorList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    if (technicianListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: technicianListData?.data?.Technicians?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (technicianListData?.code || technicianListData?.message) {
      setListState({
        isLoading: false,
        error:
          technicianListData?.message ||
          "Failed to load technician list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [technicianListData]);

  const designationOptions = designationListData?.data?.Designation || [];

  const tableData =
    technicianListData?.data?.Technicians?.map((item, index) => ({
      ...item,
      id: item?.id,
      srNo:
        (pageNum - 1) *
          (technicianListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      name: item?.name || "-",
      password: item?.password || "-",
      contractor: item?.contractor?.name || "-",
      designation: item?.designation?.name || "-",
      status: item?.status || "-",
      employeeID: item?.employeeID || "-",
      department: item?.department?.name || "-",
      createdBy: item?.createdBy || "-",
      createdAt: ISOToDDMMYYYY(item?.createdAt),
    })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.name?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name.trim()],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "DeptID",
        condition: "eq",
        filterValues: [String(filters.department)],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilters((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setFilters({
      name: "",
      department: "",
      status: "",
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const getValidationError = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        return !trimmed ? "Please enter technician name" : "";

      case "employeeID":
        return !trimmed ? "Please enter technician employee id" : "";

      case "deptId":
        return !value || !value.ID ? "Please select department" : "";

      case "gender":
        return !value ? "Please select gender" : "";

      case "designation":
        return !value || !value.ID ? "Please select designation" : "";

      case "contractorId":
        return !value || !value.ID ? "Please select contractor" : "";

      default:
        return "";
    }
  };

  const handleChange = (field, value) => {
    setTechFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setTechFormData((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: trimmedValue, error },
    }));
  };

  const validateForm = () => {
    let newData = { ...techFormData };
    Object.keys(newData).forEach((key) => {
      if (key === "id") return;
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setTechFormData(newData);
    return Object.keys(newData)
      .filter((key) => key !== "id")
      .every((key) => newData[key].error === "");
  };

  const openAddModal = () => {
    setPanelMode("add");
    setTechFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const res = await getTechnicianById(row.id);
    if (res?.code === 200) {
      const tech = res?.data;

      setTechFormData({
        id: tech?.id,

        name: {
          value: tech?.name || "",
          original: tech?.name || "",
          error: "",
        },

        employeeID: {
          value: tech?.employeeID || "",
          original: tech?.employeeID || "",
          error: "",
        },

        password: {
          value: "",
          original: "",
          error: "",
        },

        deptId: {
          value:
            departmentOptions.find((d) => d.ID === tech?.department?.ID) ||
            null,
          original: tech?.department?.ID || "",
          error: "",
        },

        gender: {
          value: tech?.gender || "",
          original: tech?.gender || "",
          error: "",
        },

        designation: {
          value:
            designationOptions.find((d) => d.ID === tech?.designation?.ID) ||
            null,
          original: tech?.designation?.ID || "",
          error: "",
        },

        contractorId: {
          value:
            contractorOptions.find((c) => c.ID === tech?.contractor?.ID) ||
            null,
          original: tech?.contractor?.ID || "",
          error: "",
        },

        status: {
          value: tech?.status || "ACTIVE",
          original: tech?.status || "ACTIVE",
          error: "",
        },
      });
    } else {
      const message = Array.isArray(res?.message)
        ? res?.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
        : res?.message || "Failed to fetch technician details";

      notify(message, "error");
    }

    setListState((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  const closeModal = () => {
    setPanelMode(null);
    setTechFormData({ ...initialFields });
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // only csv validation
    if (!file.name.toLowerCase().endsWith(".csv")) {
      notify("Please upload a valid CSV file", "error");
      e.target.value = null;
      return;
    }

    try {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
      }));

      const formData = new FormData();
      formData.append("file", file);

      const res = await importTechnician(formData);

      if (res?.code === 200) {
        notify(
          res?.message || "Technician CSV imported successfully",
          "success",
        );

        // refresh listing after successful import
        getTechnicianList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to import technician CSV";

        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while importing CSV",
        "error",
      );
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));

      // reset input so same file can be selected again
      e.target.value = null;
    }
  };

  const handleDelete = (row) => {
    showAlert({
      title: "Are you sure you want to delete this technician?",
      message: `Technician "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));

          const res = await deleteTechnicianById(row.id);

          if (res?.code === 200) {
            notify(
              res?.message || "Technician deleted successfully",
              "success",
            );

            // refresh list
            getTechnicianList(dispatch, pageNum, filters.filterParams);
          } else {
            notify(res?.message || "Failed to delete technician", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },

      onCancel: () => {},
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      let payload = {};

      Object.keys(techFormData).forEach((key) => {
        if (key === "id") return;

        const field = techFormData[key];

        let value = field.value;

        if (key === "deptId") {
          const currentId = value?.ID || "";
          if (panelMode !== "edit") {
            // Add mode  use department_id
            payload["department_id"] = currentId;
          } else if (currentId !== field.original) {
            // Edit mode use deptId
            payload["deptId"] = currentId;
          }
          return;
        }

        if (key === "contractorId") {
          const currentId = field.value?.ID || "";
          if (panelMode !== "edit" || currentId != field.original) {
            payload["contractor_id"] = currentId;
          }
          return;
        }

        if (key === "designation") {
          const currentId = field.value?.ID || "";
          if (panelMode !== "edit" || currentId != field.original) {
            payload["designation_id"] = currentId;
          }
          return;
        }

        if (key === "employeeID") {
          if (panelMode !== "edit" || value !== field.original) {
            payload["employee_id"] = value;
          }
          return;
        }

        if (panelMode !== "edit" || value !== field.original) {
          payload[key] = value;
        }
      });

      if (panelMode === "edit" && !Object.keys(payload).length) {
        notify("No changes detected", "info");
        setListState((prev) => ({
          ...prev,
          showLoaderOnClick: false,
        }));
        return;
      }

      const res =
        panelMode === "edit"
          ? await updateTechnicianById(techFormData.id, payload)
          : await addTechnician(payload);

      if (res?.code === 200) {
        notify(
          res?.message ||
            (panelMode === "edit"
              ? "Technician updated successfully"
              : "Technician added successfully"),
          "success",
        );

        closeModal();
        getTechnicianList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to save technician";

        notify(message, "error");
      }
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const renderStatusBadge = (status) => {
    const s = typeof status === "string" ? status.toUpperCase() : status;
    return (
      <MDBadge
        badgeContent={s}
        color={s === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const departmentOptions = departmentListData?.data?.Department || [];
  const columns = [
    { Header: "Sr No.", accessor: "srNo", align: "left", width: "85px" },
    {
      Header: "Name",
      accessor: "name",
      align: "left",
      width: "140px",
      Cell: (cell) => wrapCell(cell.value || "-", "150px"),
    },
    {
      Header: "Employee ID",
      accessor: "employeeID",
      align: "left",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value, "120px"),
    },
    {
      Header: "Contractor",
      accessor: "contractor",
      align: "left",
      width: "130px",
      Cell: (cell) => wrapCell(cell.value || "-", "130px"),
    },
    {
      Header: "Department",
      accessor: "department",
      align: "left",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value || "-", "120px"),
    },
    {
      Header: "Designation",
      accessor: "designation",
      align: "left",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value || "-", "120px"),
    },
    {
      Header: "Created Info",
      accessor: "createdInfo",
      align: "left",
      width: "150px",
      Cell: (cell) => {
        const { createdBy, createdAt } = cell.row.original;

        return (
          <div
            style={{
              width: "150px",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.4",
            }}
          >
            <div>{createdBy || "-"}</div>
            <div>{createdAt || "-"}</div>
          </div>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      width: "95px",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            width: "85px",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  const toggleVisibility = (field) => {
    setShow((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const downloadTechnicianCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportTechnicianData(filters.filterParams);
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Technician.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "Technician file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download technician file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = `ID,Technician Name,Employee ID,Contractor,Gender,Category,Designation,Department,Password
1,Prakash Nale,EMPLOYEE1,Raychem RPG,Male,Worker,Painter,Winding,CCA111
2,Bhushan Patil,EMPLOYEE2,Skyline,Male,Worker,Operator,CRT Process,CCA112
3,Sanjay Prajapati,EMPLOYEE3,Skyline,Female,Worker,Helper,Core Building,CCA113
4,Pradip Rathod,EMPLOYEE4,Raychem RPG,Male,Worker,Operator,Dispatch,CCA114
5,Madan Kathat,EMPLOYEE5,Sanvi Enterprises,Male,Worker,Painter,CRT Process,CCA115`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "sample_technician.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Technicians
                </MDTypography>

                <MDBox display="flex" gap={2}>
                  {canCreate && (
                    <>
                      <MDButton
                        variant="gradient"
                        color="light"
                        startIcon={<Icon>file_download</Icon>}
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                        onClick={downloadSampleCsv}
                      >
                        Sample CSV
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="light"
                        startIcon={<Icon>file_upload</Icon>}
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                        onClick={downloadTechnicianCSV}
                      >
                        {exportLoading ? (
                          <>
                            <i className="fa fa-spinner fa-spin mr-2" />
                            Exporting...
                          </>
                        ) : (
                          "Export Technician"
                        )}
                      </MDButton>

                      <MDButton
                        variant="gradient"
                        color="light"
                        component="label"
                        startIcon={<Icon>cloud_upload</Icon>}
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        Import CSV
                        <input
                          type="file"
                          hidden
                          accept=".csv"
                          onChange={handleCsvUpload}
                        />
                      </MDButton>

                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                        onClick={openAddModal}
                      >
                        Add Technician
                      </MDButton>
                    </>
                  )}
                </MDBox>
              </MDBox>

              {/* Filters (Exact Same UI Pattern) */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by name"
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                    />
                  </Grid>

                  {/* Department Filter */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={departmentOptions || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        departmentOptions.find(
                          (dept) => dept.ID === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.ID || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        IconComponent={ArrowDropDown}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        renderValue={(selected) =>
                          !selected ? (
                            <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                              Select Status
                            </span>
                          ) : (
                            renderStatusBadge(selected)
                          )
                        }
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {technicianListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No technicians found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <DataTable
                      columns={columns}
                      data={tableData}
                      isSorted={true}
                      pageNum={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (technicianListData?.data?.totalRecords || 0) /
                          (technicianListData?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                      )}
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Drawer */}
        <CenterPopup
          isOpen={panelMode !== null}
          onClose={closeModal}
          width={650}
        >
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              {panelMode === "edit" ? "Edit Technician" : "Add Technician"}
            </MDTypography>

            {panelMode === "edit" && (
              <MDBox display="flex" gap={2} mb={2}>
                <MDButton
                  variant={activeTab === "info" ? "contained" : "outlined"}
                  color="deepBlue"
                  onClick={() => setActiveTab("info")}
                >
                  Info
                </MDButton>

                <MDButton
                  variant={activeTab === "password" ? "contained" : "outlined"}
                  color="deepBlue"
                  onClick={() => setActiveTab("password")}
                >
                  Update Password
                </MDButton>
              </MDBox>
            )}

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                {panelMode === "edit" && activeTab === "password" ? (
                  <Grid item xs={12} md={12}>
                    <RequiredLabel>Password</RequiredLabel>

                    <MDInput
                      name="password"
                      placeholder="Enter password"
                      type={show.password ? "text" : "password"}
                      value={techFormData.password.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => toggleVisibility("password")}
                              edge="end"
                            >
                              <Icon>
                                {show.password
                                  ? "visibility_off"
                                  : "visibility"}
                              </Icon>
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <MDTypography variant="caption" color="text">
                      Leave blank if you don’t want to change password
                    </MDTypography>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} md={6}>
                      <RequiredLabel required>Name</RequiredLabel>
                      <MDInput
                        name="name"
                        placeholder="Enter technician name"
                        value={techFormData.name.value}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        error={!!techFormData.name.error}
                        fullWidth
                      />
                      {techFormData.name.error && (
                        <MDTypography variant="caption" color="error">
                          {techFormData.name.error}
                        </MDTypography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <RequiredLabel required>Contractor</RequiredLabel>

                      <Autocomplete
                        {...autoCompleteProps}
                        size="small"
                        options={contractorOptions.filter(
                          (c) => c.status === "ACTIVE",
                        )}
                        getOptionLabel={(option) => option.name || ""}
                        value={techFormData.contractorId.value || null}
                        onChange={(e, newValue) =>
                          handleChange("contractorId", newValue)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.ID === value?.ID
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Contractor"
                            error={!!techFormData.contractorId.error}
                            helperText={techFormData.contractorId.error}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <RequiredLabel required>Gender</RequiredLabel>

                      <Autocomplete
                        {...autoCompleteProps}
                        size="small"
                        disablePortal
                        options={GENDER || []}
                        getOptionLabel={(option) => option.label || ""}
                        value={
                          GENDER.find(
                            (g) =>
                              g.value ===
                                String(
                                  techFormData.gender.value,
                                ).toUpperCase() ||
                              g.value ===
                                (String(
                                  techFormData.gender.value,
                                ).toUpperCase() === "M"
                                  ? "MALE"
                                  : String(
                                        techFormData.gender.value,
                                      ).toUpperCase() === "F"
                                    ? "FEMALE"
                                    : ""),
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          handleChange("gender", newValue?.value || "")
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Gender"
                            error={!!techFormData.gender.error}
                            helperText={techFormData.gender.error}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <RequiredLabel required>Department</RequiredLabel>

                      <Autocomplete
                        {...autoCompleteProps}
                        size="small"
                        disablePortal
                        options={departmentOptions.filter(
                          (d) => d.status === "ACTIVE",
                        )}
                        getOptionLabel={(option) => option.name || ""}
                        value={techFormData.deptId.value || null}
                        onChange={(event, newValue) =>
                          handleChange("deptId", newValue)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.ID === value?.ID
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Department"
                            error={!!techFormData.deptId.error}
                            helperText={techFormData.deptId.error}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <RequiredLabel required>Employee Id</RequiredLabel>
                      <MDInput
                        name="employeeID"
                        placeholder="Enter employee id"
                        value={techFormData.employeeID.value}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        error={!!techFormData.employeeID.error}
                        fullWidth
                      />
                      {techFormData.employeeID.error && (
                        <MDTypography variant="caption" color="error">
                          {techFormData.employeeID.error}
                        </MDTypography>
                      )}
                    </Grid>

                    {panelMode === "add" && (
                      <Grid item xs={12} md={6}>
                        <RequiredLabel required>Password</RequiredLabel>
                        <MDInput
                          name="password"
                          placeholder="Enter password"
                          type={show.password ? "text" : "password"}
                          value={techFormData.password.value}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.value)
                          }
                          onBlur={(e) =>
                            handleOnBlur(e.target.name, e.target.value)
                          }
                          error={!!techFormData.password.error}
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => toggleVisibility("password")}
                                  edge="end"
                                >
                                  <Icon>
                                    {show.password
                                      ? "visibility_off"
                                      : "visibility"}
                                  </Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {techFormData.password.error && (
                          <MDTypography variant="caption" color="error">
                            {techFormData.password.error}
                          </MDTypography>
                        )}
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <RequiredLabel>Designation</RequiredLabel>
                      <Autocomplete
                        {...autoCompleteProps}
                        size="small"
                        options={designationOptions.filter(
                          (d) => d.status === "ACTIVE",
                        )}
                        getOptionLabel={(option) => option.name || ""}
                        value={techFormData.designation.value || null}
                        onChange={(e, newValue) =>
                          handleChange("designation", newValue || "")
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.ID === value?.ID
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Designation"
                            error={!!techFormData.designation.error}
                            helperText={techFormData.designation.error}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <RequiredLabel>Status</RequiredLabel>
                      <RadioGroup
                        row
                        name="status"
                        value={techFormData.status.value}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="ACTIVE"
                          control={<Radio size="small" />}
                          label="Active"
                          sx={radioLabelSx}
                        />
                        <FormControlLabel
                          value="INACTIVE"
                          control={<Radio size="small" />}
                          label="Inactive"
                          sx={radioLabelSx}
                        />
                      </RadioGroup>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default TechnicianListing;
